import { APIApplication, FlowMetadata } from './application';
import { EmptyString, Nullable, User, UserLanguage } from '.';
import { FactoringRequest } from './factoring';
import { BankingAccount } from './banking';

export type PipelineStatus =
  | 'application_sent'
  | 'application_received'
  | 'termsheet_sent'
  | 'termsheet_received_and_document_collection'
  | 'due_dilligence'
  | 'credit_committee'
  | 'legal'
  | 'onboarding'
  | 'closed';

export type InternalPipelineStatus = PipelineStatus | 'credit_committee_advanced';

export type CompanyUserStatus =
  | 'signup_started'
  | 'signup_completed'
  | 'application_started'
  | 'application_completed'
  | 'termsheet_sent'
  | 'termsheet_signed'
  | 'due_diligence'
  | 'credit_committee'
  | 'legal'
  | 'onboarding'
  | 'closed_won'
  | 'closed_lost';

export type APINotification = {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: Nullable<string>;
  title: string;
  text: string;
  status: Nullable<'READ' | 'PENDING'>;
  actionType: Nullable<'UPLOAD' | 'MESSAGE'>;
  companyUser: Nullable<number>;
};

export type ThirdPartyConnections = {
  satws: boolean;
};

export type CompanyApplications = {
  company: Company;
  applications: APIApplication[];
  applicationCompletion: number;
  signupLink: Nullable<string>;
  status: Nullable<string>;
};

export type TaxIdStatus = 'unverified' | 'verified' | 'rejected';

export enum SatCredentialStatus {
  Waiting = 'waiting',
  Pending = 'pending',
  Valid = 'valid',
  Invalid = 'invalid',
  Disabled = 'disabled',
}

export enum CompanyOnboardingStep {
  Initial = 'initial',
  CountrySelection = 'country_selection',
  CompanyInformation = 'company_information',
  DocumentsUpload = 'documents_upload',
  OwnershipStructure = 'ownership_structure',
  Completed = 'completed',
}

export enum OnboardingStage {
  Screening = 'screening',
  ManualReview = 'manualReview',
  Application = 'application',
  Verification = 'verification',
  ActionRequired = 'actionRequired',
  Terms = 'terms',
  Active = 'active',
}

export enum OnboardingTrackerStep {
  Setup = 'setup',
  Application = 'application',
  Verification = 'verification',
  Start = 'start',
}

export enum AipriseDecision {
  Pending = 'pending',
  AutoApproved = 'auto_approved',
  ManualReviewRequired = 'manual_review_required',
}

export type ProductStatus = {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: Nullable<string>;
  readyForFactoring: boolean;
  readyForBanking: boolean;
  hasStartedBanking: boolean;
  showRcl: boolean;
  showFactoringCreditLine: boolean;
  readyForBankingAccountCreation: boolean;
  readyForCompliance: boolean;
  readyForInvoicing: boolean;
  hasAccounting: boolean;
  complianceNotified: boolean;
  hasStartedCompliance: boolean;
  factoringFlowMetadata: FlowMetadata;
  bankingFlowMetadata: any;
  hasComplianceRequest: boolean;
  hasBankingRequest: boolean;
  hasInvoiceFinancingRequest: boolean;
  hasInvoicingRequest: boolean;
  hasTotalComplianceRequest: boolean;
  satCredentialStatus: Nullable<SatCredentialStatus>;
};

export type AipriseEvent = {
  eventType:
    | 'BUSINESS_PROFILE_CREATE'
    | 'ADD_ADDITIONAL_MEDIA'
    | 'RUN_BUSINESS_DOCUMENT_CHECK'
    | 'ADD_BUSINESS_OFFICER'
    | 'BUSINESS_OFFICER_RUN_KYC'
    | 'RUN_BUSINESS_VERIFICATION'
    | 'RESULT_UPDATE'
    | 'MAKE_DECISION'
    | 'UNKNOWN';
  result: string | null;
  status: string | null;
  score: string | null;
  authorId: string | null;
  personReferenceId: string | null;
  personName: string | null;
  notes: string | null;
  createdAt: string;
};

export type AipriseValidation = {
  state: 'pending' | 'trigger_failed' | 'completed';
  decision: AipriseDecision;
  result: string | null;
  score: string | null;
  notes: string | null;
  submittedAt: string | null;
  completedAt: string | null;
  events: AipriseEvent[];
};

export type Company = {
  id: number;
  productStatus: ProductStatus;
  aipriseValidation: AipriseValidation | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: Nullable<string>;
  uuid: string;
  validated: boolean;
  name: string;
  taxId: Nullable<string>;
  taxIdStatus: TaxIdStatus;
  taxIdManuallyVerified: boolean;
  institutionalPhone: Nullable<string>;
  institutionalEmail: Nullable<string>;
  institutionalWebsite: Nullable<string>;
  addressStreetOne: Nullable<string>;
  addressStreetTwo: Nullable<string>;
  addressZipCode: Nullable<string>;
  addressCity: Nullable<string>;
  addressState: Nullable<string>;
  country: string; // in its short form
  taxIdManuallyVerifiedBy: Nullable<number>;
  initialUser: CompanyUser;
  signupLink: Nullable<string>;
  formationDate: Nullable<string>;
  taxIdType: Nullable<string>;
  companyUserRoles: CompanyUserRole[];
  businessStructure: Nullable<string>;
  formationState: Nullable<string>;
  accounts: BankingAccount[];
  cashControlAccounts: BankingAccount[];
  industry: Nullable<string>;
  agreementSignatureDate: Nullable<string>;
  // only in frontend
  factoringRequest: Nullable<FactoringRequest>;
  internalPipelineStatus: EmptyString<InternalPipelineStatus>;
  isDraft: boolean;
  onboardingStep: Nullable<CompanyOnboardingStep>;
  formedFromLlc: boolean;
  referralId: Nullable<string>;
};

export type CompanyAdminResult = {
  uuid: string;
  userName: string;
  name: string;
  updatedAt: string;
  taxId: Nullable<string>;
  id: number;
  country: string;
  productStatus: ProductStatus;
};

export type CompanyAdminFilters = {
  company: string;
  ordering: string;
  products: string[];
  subProducts: string[];
  page: number;
  pageSize: number;
};

export type InvoiceFinancingCompanyAdminResult = {
  uuid: string;
  userName: string;
  name: string;
  stage: string;
  status: string;
  updatedAt: string;
  taxId: Nullable<string>;
  id: number;
  country: string;
};

export type InvoiceFinancingCompanyAdminFilters = {
  company: string;
  ordering: string;
  status: string[];
  deletedOnly: boolean;
  page: number;
  pageSize: number;
};

export enum UserRole {
  GENERIC = 'generic',
  COMPANY_CREATOR = 'company_creator',
  ADMINISTRATOR = 'administrator',
  COLLABORATOR = 'collaborator',
  OWNER = 'owner',
  VISUALIZER = 'visualizer',
}

export type CompanyUserInvitation = {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: null;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  userPhone: string;
  role: UserRole;
  isConfirmed: false;
  language: UserLanguage;
  invitingUser: number;
  company: number;
  jobTitle: string;
};

export type CompanyUserRole = {
  id: number;
  user: CompanyUser;
  role: UserRole;
  company: Company;
  jobTitle: string;
};

export type CompanyUser = {
  id: number;
  draftCompany: Nullable<Company>;
  selectedCompany: Company;
  selectedCompanyRole?: UserRole;
  companies: Company[];
  companyUserRoles: CompanyUserRole[];
  user: User;
  createdAt: string;
  updatedAt: string;
  deletedAt: Nullable<string>;
  name: string;
  contactPhone: string;
  referralId: Nullable<string>;
  referralName: Nullable<string>;
  utmLandingPage: Nullable<string>;
  notificationPreferences: {
    generalNotifications: boolean;
    paymentNotifications: boolean;
    fundingRequestNotifications: boolean;
    bankingNotifications: boolean;
  };
  uuid: string;
};

export interface AdminCreateCompanyPayload {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  companyName: string;
  country: string;
  sendPasswordEmail: boolean;
}
